import { Shield } from 'lucide-react';
import { useId } from 'react';
import type { NetworkKinds } from '../../api/types';
import { strings } from '../../strings';
import { AWS_CENTRAL_EGRESS, money } from './helpers';

const t = strings.networks.topology;

/**
 * New network's AWS Network Firewall (feature spec 17, phase 4; spec 11 6.15.31): a switch with the firewall's price per
 * region and, with egress through it (central egress), the central NAT gateways' price. Choosing hub-spoke turns both on in
 * the form with the saving of turning them off; a /26 hub can't hold central egress.
 */
export function AwsFirewallChoice({
  entry,
  on,
  central,
  byHubSpoke,
  regions,
  hubTooSmall,
  onChange,
}: {
  entry: NetworkKinds['firewalls'][number];
  on: boolean;
  central: boolean;
  /** Hub-spoke is chosen (the firewall was turned on by that choice). */
  byHubSpoke: boolean;
  regions: number;
  hubTooSmall: boolean;
  onChange: (next: { awsFirewall?: boolean; awsCentralEgress?: boolean }) => void;
}) {
  const ids = useId();
  const cost = Object.values(entry.tierCosts ?? {})[0];
  const sum = cost ? (cost.monthly + (central ? AWS_CENTRAL_EGRESS.monthly : 0)) * Math.max(1, regions) : 0;
  return (
    <fieldset className="net-provider-block mt-4" data-testid="new-network-aws-firewall">
      <legend className="sr-only">{t.awsFirewall}</legend>
      <label className="net-switch-label flex items-center gap-2 font-semibold">
        <input
          type="checkbox"
          role="switch"
          checked={on}
          aria-describedby={`${ids}-help`}
          onChange={(e) =>
            onChange({ awsFirewall: e.target.checked, awsCentralEgress: e.target.checked && !hubTooSmall })
          }
        />
        <Shield aria-hidden className="size-4 text-fg-muted" />
        {t.awsFirewall}
      </label>
      <p id={`${ids}-help`} className="help mt-1">
        {cost && <span className="block">{t.awsFirewallCost(money(cost), cost.asOf)}</span>}
        {on && central && <span className="block">{t.awsCentralEgressCost(money(AWS_CENTRAL_EGRESS))}</span>}
        {on && byHubSpoke && cost && (
          <span className="block">
            {t.awsTurnedOnForHubSpoke(money({ monthly: sum, currency: cost.currency }))}
          </span>
        )}
        {!on && <span className="block">{t.firewallOffHelp}</span>}
      </p>
      {on && (
        <label className="mt-2 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            checked={central}
            disabled={hubTooSmall}
            aria-describedby={hubTooSmall ? `${ids}-small` : undefined}
            onChange={(e) => onChange({ awsCentralEgress: e.target.checked })}
          />
          <span>
            <span className="font-medium">{t.awsEgressFirewall}</span>
            <span className="block text-[12.5px] text-fg-muted">{t.awsEgressFirewallHelp}</span>
            {hubTooSmall && (
              <span id={`${ids}-small`} className="block text-[12.5px] text-warning">
                {t.awsHubTooSmall}
              </span>
            )}
          </span>
        </label>
      )}
    </fieldset>
  );
}
